import { useState } from 'react'

import { usePanoramaContext } from '@routes/Panorama/PanoramaImageContext'
import { type PanoramaNetwork, usePanoramaNetwork } from '@routes/Panorama/data/panoramaNetwork'
import { type PanoramaImage, usePanorama } from '@state/panorama'
import { isDevMode, isIntegrationMode } from '@utils/env'

import UserIndicator from './UserIndicator'

const DEBUG = isDevMode || isIntegrationMode

function PanoramaLayer() {
  const { currentImage, selectImage } = usePanoramaContext()
  const { data: panoramaImages } = usePanorama()
  const { data: panoramaNetwork } = usePanoramaNetwork()

  if (panoramaImages === undefined || panoramaNetwork === undefined) return null

  return (
    <g>
      <Path network={panoramaNetwork} />

      {panoramaImages.map((image, i) => {
        return (
          <PanoramaCircle
            key={`panorama-circle-${i}`}
            onSelect={selectImage}
            panoramaImage={image}
          />
        )
      })}

      {currentImage && <UserIndicator image={currentImage} />}
    </g>
  )
}

interface PanoramaCircleProps {
  panoramaImage: PanoramaImage
  onSelect: (panoramaImage: PanoramaImage) => void
}

function PanoramaCircle({ panoramaImage, onSelect }: PanoramaCircleProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered && (
        <circle
          cx={panoramaImage.x}
          cy={panoramaImage.y}
          fill='var(--color-brand-300)'
          r={1.5}
          opacity={0.5}
          style={{ cursor: 'pointer' }}
        />
      )}

      <circle
        cx={panoramaImage.x}
        cy={panoramaImage.y}
        fill="var(--color-brand-500)"
        r={0.5}
        style={{ cursor: 'pointer' }}
        onClick={() => onSelect(panoramaImage)}
        data-testid={`panorama-circle-${panoramaImage.id}`}
      />

      {DEBUG && (
        <text
          x={panoramaImage.x + 0.4}
          y={panoramaImage.y - 0.4}
          fill='black'
          fontSize={0.3}
        >
          {`${panoramaImage.id} (${panoramaImage.x.toFixed(2)}, ${panoramaImage.y.toFixed(2)})`}
        </text>
      )}
    </g>
  )
}

interface PathProps {
  network: PanoramaNetwork
}

function Path({ network }: PathProps) {
  return (
    <>
      {
        network.edges.map(pfad => {
          return (
            <>
              <line
                key={`pano-edge-${pfad.from.x}-${pfad.from.y}-to-${pfad.to.x}-${pfad.to.y}`}
                x1={pfad.from.x}
                y1={pfad.from.y}
                x2={pfad.to.x}
                y2={pfad.to.y}
                stroke='var(--color-brand-100)'
                strokeWidth={0.5}
              />

              <line
                key={`pano-edge-sec-${pfad.from.x}-${pfad.from.y}-to-${pfad.to.x}-${pfad.to.y}`}
                x1={pfad.from.x}
                y1={pfad.from.y}
                x2={pfad.to.x}
                y2={pfad.to.y}
                stroke='var(--color-brand-500)'
                strokeWidth={0.1}
              />
            </>
          )
        })
      }
    </>
  )
}

export default PanoramaLayer
